// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { clearSearchHistory, getSearchHistory } from "@insite/client-framework/Services/AutocompleteService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    focusedQuery?: string;
    goToUrl: (url: string) => void;
    extendedStyles?: SearchHistoryStyles;
    closeAutoCompleteAndPopover?: any;
}

const mapStateToProps = (state: ApplicationState) => ({
    searchHistoryEnabled: getSettingsCollection(state).searchSettings.searchHistoryEnabled,
    searchPath: getSettingsCollection(state).searchSettings.searchPath,
    customSettings: getSettingsCollection(state).customSettings as any,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface SearchHistoryStyles {
    headerText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    itemWrapper?: InjectableCss;
    icon?: IconPresentationProps;
    focusedLink?: LinkPresentationProps;
    clearLink?: LinkPresentationProps;
    link?: LinkPresentationProps;
    leftColumn?: InjectableCss;
    rightColumn?: InjectableCss;
}

export const searchHistoryStyles: SearchHistoryStyles = {
    headerText: {
        size: fontConfig.fontSize.xSmall,
        weight: fontConfig.weight.semiBold,
        lineHeight: fontConfig.lineHeight.small,
        transform: "uppercase",
        color: "text.main",
        css: css`
            letter-spacing: 0.2px;
        `,
    },
    link: {
        typographyProps: {
            size: fontConfig.fontSize.regular,
            weight: fontConfig.weight.semiBold,
            lineHeight: "20px",
            color: "text.main",
            css: css`
                letter-spacing: 0.2px;
                text-align: left;
            `,
        },
        color: "text.main",
    },
    wrapper: {
        css: css`
            display: flex;
            padding-top: 39px;
            padding-bottom: 39px;
        `,
    },
    leftColumn: {
        css: css`
            width: 135px;
            margin-right: 35px;
        `,
    },
    rightColumn: {
        css: css`
            width: calc(100% - 170px);
        `,
    },
    itemWrapper: {
        css: css`
            display: flex;
            align-items: center;
            margin-bottom: 10px;
        `,
    },
    icon: {
        src: "Clock",
        css: css`
            margin-right: 10px;
        `,
    },

    clearLink: {
        typographyProps: {
            css: css`
                width: 100%;
                text-align: right;
            `,
        },
    },
};

const styles = searchHistoryStyles;

class SearchHistory extends React.Component<Props> {
    private readonly styles: SearchHistoryStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    clearLinkClickHandler = () => {
        clearSearchHistory();
        this.forceUpdate();
        this.props.closeAutoCompleteAndPopover();
    };

    render() {
        if (!this.props.searchHistoryEnabled) {
            return null;
        }

        const searchHistory = getSearchHistory();
        if (searchHistory.length === 0) {
            return null;
        }

        const styles = this.styles;
        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.leftColumn}>
                    <Typography {...styles.headerText}>{translate("Search History")}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.rightColumn}>
                    {searchHistory.map(searchHistoryItem => (
                        <StyledWrapper {...styles.itemWrapper} key={searchHistoryItem.query}>
                            <IconMemo {...styles.icon} />
                            <Link
                                {...styles.link}
                                onClick={() =>
                                    this.props.goToUrl(
                                        this.props.customSettings.attributeAutoFilter
                                            ? `/${this.props.searchPath}?query=${encodeURIComponent(searchHistoryItem.query)}&attributeValueIds=${encodeURIComponent(this.props.customSettings.attributeAutoFilter)}`
                                            : `/${this.props.searchPath}?query=${encodeURIComponent(searchHistoryItem.query)}`,
                                    )
                                }
                            >
                                {searchHistoryItem.query}
                            </Link>
                        </StyledWrapper>
                    ))}
                    <Link {...styles.clearLink} color="primary" onClick={this.clearLinkClickHandler}>
                        {translate("Clear Search History")}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

export default connect(mapStateToProps)(SearchHistory);
